import { ChangeEvent, Dispatch, ReactNode, SetStateAction } from 'react';
import {
  ClickAwayListener,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  TextField,
  useTheme,
} from '@mui/material';
import { Icons, HXCIcon } from '../../HXCIcon';

export interface SearchBarProps {
  /*
   * Callback for searching
   */
  onSearch?: (text: string) => void;
  /*
   * Options for displaying in the search dropdown ALREADY WRAPPED IN MENU ITEMS
   */
  searchOptions?: ReactNode[];
  /*
   * Placeholder text for the search bar
   */
  searchPlaceholder?: string;
  /*
   * Change handler for search
   */
  handleSearch: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /*
   * String input into search bar
   */
  searchTerm: string;
  /*
   * Anchor for search options
   */
  anchorEl: HTMLElement | null;
  /*
   * Setter for search anchor
   */
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}

export const SearchBar = ({
  onSearch,
  searchPlaceholder = 'Search',
  handleSearch,
  searchTerm,
  searchOptions,
  anchorEl,
  setAnchorEl,
}: SearchBarProps) => {
  const theme = useTheme();

  return (
    <>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={searchPlaceholder}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (onSearch && ((searchTerm?.length ?? 0) > 2 || searchTerm?.length === 0)) {
              onSearch(searchTerm);
            }
          }
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <HXCIcon name={Icons.SearchSm} color={'lightGray'} />
              </InputAdornment>
            ),
            style: {
              borderRadius: 8,
              color: theme.palette.white.main,
              backgroundColor: theme.palette.header.globalNavBackground,
              maxHeight: '40px',
            },
            'aria-label': searchPlaceholder,
          },
        }}
        onChange={handleSearch}
        value={searchTerm}
        sx={{
          '& .MuiOutlinedInput-input': {
            backgroundColor: theme.palette.header.globalNavBackground,
          },
        }}
      />
      {Boolean(searchOptions?.length) && (
        <Popper
          sx={{ zIndex: 1101 }}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
        >
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Paper
              sx={{
                '& .MuiMenuItem-root': {
                  padding: '8px 16px',
                },
              }}
            >
              {searchOptions?.map((option, index) => (
                <MenuItem key={index.toString()}>{option}</MenuItem>
              ))}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};
